import { Link, useNavigate } from "react-router-dom";
import {
  Activity, ArrowRight, LineChart, LayoutGrid, BellRing, Share2,
  FileDown, Radio, Check, Zap,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const FEATURES = [
  { icon: LineChart, title: "Interactive visuals", desc: "Charts, gauges, sparklines and graphs that make every metric instantly legible." },
  { icon: LayoutGrid, title: "Drag-and-drop layouts", desc: "Build personalized dashboards with resizable, reorderable widgets." },
  { icon: Radio, title: "Real-time streaming", desc: "Live WebSocket updates keep your numbers moving second by second." },
  { icon: BellRing, title: "Threshold alerts", desc: "Automated Slack & email triggers the moment a metric crosses a limit." },
  { icon: FileDown, title: "Export & schedule", desc: "One-click CSV/PDF exports and automated daily or weekly reports." },
  { icon: Share2, title: "Role-based sharing", desc: "Secure, shareable links scoped to viewer or editor access." },
];

const PLANS = [
  { id: "starter", name: "Starter", price: 19, points: ["Real-time dashboard", "Charts, gauges & sparklines", "Global + widget filters", "CSV export"] },
  { id: "pro", name: "Pro", price: 49, highlight: true, points: ["Everything in Starter", "Drag-and-drop layouts", "Slack + email alerts", "Scheduled reports", "Shareable links"] },
];

function DashboardMock() {
  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-2xl shadow-black/40" data-testid="hero-mock">
      <div className="flex items-center gap-1.5 mb-3 px-1">
        <span className="h-2.5 w-2.5 rounded-full bg-[hsl(0_72%_51%)]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[hsl(45_93%_47%)]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[hsl(142_71%_45%)]" />
        <span className="ml-2 text-[10px] font-mono text-muted-foreground">pulse / executive overview</span>
        <span className="ml-auto inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[hsl(142_71%_55%)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[hsl(142_71%_45%)] animate-pulse" />live
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[["Revenue", "$133K", "+11.3%", true], ["Users", "8,589", "+0.3%", true], ["Conv.", "3.16%", "-1.4%", false], ["NPS", "54", "+2.1%", true]].map(([l, v, c, up]) => (
          <div key={l} className="rounded-md border border-border bg-background p-2">
            <div className="text-[8px] uppercase tracking-widest text-muted-foreground">{l}</div>
            <div className="tnum text-sm font-bold mt-0.5">{v}</div>
            <div className={`text-[9px] font-mono ${up ? "text-[hsl(142_71%_55%)]" : "text-destructive"}`}>{c}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2">
        <div className="col-span-2 rounded-md border border-border bg-background p-2 h-28">
          <div className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">Revenue trend</div>
          <svg viewBox="0 0 200 70" className="w-full h-[76px]" preserveAspectRatio="none">
            <polyline fill="none" stroke="hsl(221 83% 53%)" strokeWidth="2"
              points="0,45 20,40 40,48 60,32 80,38 100,28 120,34 140,22 160,26 180,16 200,20" />
          </svg>
        </div>
        <div className="rounded-md border border-border bg-background p-2 h-28 flex flex-col">
          <div className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">By region</div>
          <div className="flex items-end gap-1.5 flex-1">
            {[60, 80, 45, 90].map((h, i) => (
              <div key={`bar-${h}-${i}`} className="flex-1 rounded-t" style={{ height: `${h}%`, background: ["hsl(221 83% 53%)", "hsl(142 71% 45%)", "hsl(45 93% 47%)", "hsl(280 65% 60%)"][i] }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { user, subscription } = useAuth();
  const navigate = useNavigate();
  const loggedIn = !!user;
  const hasAccess = subscription?.has_access;

  const primaryCta = () => {
    if (hasAccess) return navigate("/dashboard");
    if (loggedIn) return navigate("/pricing");
    return navigate("/register");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <Activity className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold tracking-tight">PULSE / KPI</span>
          </div>
          <nav className="flex items-center gap-2">
            <a href="#features" className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground transition-colors px-3">Features</a>
            <a href="#pricing" className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground transition-colors px-3">Pricing</a>
            {loggedIn ? (
              <Button size="sm" onClick={() => navigate(hasAccess ? "/dashboard" : "/pricing")} data-testid="nav-dashboard-button">
                {hasAccess ? "Dashboard" : "Choose a plan"}
              </Button>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-signin-button">Sign in</Button></Link>
                <Link to="/register"><Button size="sm" data-testid="nav-getstarted-button">Get started</Button></Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 opacity-[0.035]" style={{ backgroundImage: "linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 mb-6">
              <Zap className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Real-time KPI command center</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Every metric.<br />One truth.<br /><span className="text-primary">Real time.</span>
            </h1>
            <p className="text-muted-foreground mt-6 text-base sm:text-lg max-w-lg">
              Build live dashboards your whole team trusts — interactive charts, drag-and-drop widgets, threshold alerts, and scheduled reports. Set up in minutes.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Button size="lg" className="group" onClick={primaryCta} data-testid="hero-cta-button">
                {hasAccess ? "Open your dashboard" : loggedIn ? "Choose a plan" : "Start 7-day free trial"}
                <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
              </Button>
              <a href="#features"><Button size="lg" variant="outline" data-testid="hero-secondary-button">See features</Button></a>
            </div>
            <p className="text-xs text-muted-foreground mt-4 font-mono">No credit card charged during trial · cancel anytime</p>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-full" />
            <div className="relative"><DashboardMock /></div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Everything you need</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight max-w-xl">A dashboard that keeps up with your business.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-lg border border-border bg-card p-6 hover:border-primary/50 transition-colors" data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
              <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-heading text-lg font-bold tracking-tight">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
          {[["3s", "Live refresh"], ["8+", "Built-in KPIs"], ["2", "Alert channels"], ["7 days", "Free trial"]].map(([n, l]) => (
            <div key={l}>
              <div className="tnum text-3xl font-bold tracking-tight text-primary">{n}</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Simple pricing · 7-day free trial</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Pick a plan and start free.</h2>
        <div className="grid md:grid-cols-2 gap-5 mt-10">
          {PLANS.map((p) => (
            <div key={p.id} className={`relative rounded-lg border p-6 flex flex-col ${p.highlight ? "border-primary bg-primary/5" : "border-border bg-card"}`} data-testid={`landing-plan-${p.id}`}>
              {p.highlight && <span className="absolute -top-2.5 left-6 text-[10px] uppercase tracking-widest bg-primary text-primary-foreground px-2 py-0.5 rounded">Most popular</span>}
              <h3 className="font-heading text-xl font-bold tracking-tight">PULSE {p.name}</h3>
              <div className="mt-3 mb-6 flex items-end gap-1">
                <span className="tnum text-4xl font-bold tracking-tight">${p.price}</span>
                <span className="text-muted-foreground mb-1 text-sm">/month</span>
              </div>
              <ul className="space-y-2.5 flex-1">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2 text-sm">
                    <Check className={`h-4 w-4 mt-0.5 shrink-0 ${p.highlight ? "text-primary" : "text-[hsl(142_71%_55%)]"}`} />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
              <Button className="w-full mt-6" variant={p.highlight ? "default" : "outline"} onClick={primaryCta} data-testid={`landing-cta-${p.id}`}>
                Start free trial
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Ready to see everything at a glance?</h2>
          <p className="text-muted-foreground mt-3 max-w-md mx-auto">Spin up your command center in minutes. Free for 7 days.</p>
          <Button size="lg" className="mt-8 group" onClick={primaryCta} data-testid="footer-cta-button">
            {loggedIn ? (hasAccess ? "Open dashboard" : "Choose a plan") : "Get started free"}
            <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Activity className="h-4 w-4 text-primary" /> PULSE / KPI
          </div>
          <p className="text-xs text-muted-foreground font-mono">© 2026 Pulse Analytics · Secure checkout by Stripe</p>
        </div>
      </footer>
    </div>
  );
}
